import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import './About.css';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

const About = () => {
  return (
    <main className="about-page">
      {/* HERO */}
      <section className="about-hero">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
              About Us
            </div>
            <h1 className="section-title" style={{ color: 'var(--white)', fontSize: 'clamp(30px,5vw,52px)' }}>
              A New Chapter in Indian<br />Agricultural Exports
            </h1>
            <p className="section-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Founded in 2024, we are a passionate new-generation merchant exporter from the heartland of Uttar Pradesh.
            </p>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="about-story">
        <div className="container">
          <div className="story-grid">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="section-tag">Our Story</div>
              <h2 className="section-title">From the Heart of<br />Uttar Pradesh to the World</h2>
              <p className="story-text">
                From the fertile heartland of Uttar Pradesh, Seth Mr. Sanwariya Agro Primes was born
                with a singular ambition — to carry India's finest agricultural produce to dinner tables
                across the world.
              </p>
              <p className="story-text">
                Founded in October 2024 by Sujal Gupta, we are a new-generation merchant exporter
                committed to uncompromising quality, transparent trade, and building long-term global
                partnerships. We obtained our IEC (Import Export Code) in May 2026, formally stepping
                onto the global trade stage.
              </p>
              <p className="story-text">
                Every product we export carries the trust of Indian farming heritage, backed by modern
                processing standards and international-grade packaging. We don't just export food —
                we export confidence.
              </p>
            </motion.div>

            <motion.div
              className="story-cards"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              custom={0.1}
              variants={fadeUp}
            >
              {[
                { year: 'Oct 2024', label: 'Company Founded', icon: '🏢', desc: 'Seth Mr. Sanwariya Agro Primes established in Kasganj, UP' },
                { year: 'May 2026', label: 'IEC Certified', icon: '📋', desc: 'Import Export Code granted by DGFT — official global trade authorization' },
                { year: '2026', label: 'FSSAI & APEDA', icon: '✅', desc: 'Certified under FSSAI and registered with APEDA for export compliance' },
                { year: 'Future', label: 'ISO Certification', icon: '🎯', desc: 'ISO quality management system certification actively being pursued' },
              ].map((item, i) => (
                <div key={item.year} className="timeline-card">
                  <div className="timeline-icon">{item.icon}</div>
                  <div>
                    <div className="timeline-year">{item.year}</div>
                    <div className="timeline-label">{item.label}</div>
                    <div className="timeline-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="founder-section-full">
        <div className="container">
          <motion.div
            className="founder-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className="founder-avatar-lg">SG</div>
            <div className="founder-content">
              <div className="founder-badge">Founder & Director</div>
              <h2 className="founder-name-lg">Sujal Gupta</h2>
              <p className="founder-bio">
                Sujal Gupta is the founding force behind Seth Mr. Sanwariya Agro Primes. With a clear
                vision of connecting India's agricultural richness with global markets, he established
                this company to bridge the gap between quality Indian produce and international buyers
                who demand consistency, reliability, and transparency.
              </p>
              <blockquote className="founder-quote-lg">
                "Our goal is simple — bring the purity of Indian agriculture to every corner of the world,
                without compromise. We are new, but our commitment to quality and honesty is absolute."
              </blockquote>
              <div className="founder-links">
                <a
                  href="https://www.linkedin.com/in/sujal-gupta-exports"
                  target="_blank"
                  rel="noreferrer"
                  className="founder-linkedin"
                >
                  Connect on LinkedIn →
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MVV */}
      <section className="mvv-section">
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ margin: '0 auto 16px' }}>Our Foundation</div>
            <h2 className="section-title">Mission, Vision & Values</h2>
          </motion.div>
          <div className="mvv-grid">
            {[
              {
                icon: '🎯', title: 'Our Mission',
                text: 'To deliver premium Indian agro products to global buyers with integrity, quality assurance, and timely fulfillment — making each shipment a statement of trust.',
              },
              {
                icon: '🌐', title: 'Our Vision',
                text: 'To become a trusted and recognized name in international agro-trade, representing India\'s agricultural excellence across every continent.',
              },
              {
                icon: '💎', title: 'Our Values',
                text: 'Quality first. Transparent dealings. Long-term partnerships. Farmer-to-world traceability. Continuous improvement in product and process.',
              },
            ].map((m, i) => (
              <motion.div
                key={m.title}
                className="mvv-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.1}
                variants={fadeUp}
              >
                <div className="mvv-icon">{m.icon}</div>
                <h3 className="mvv-title">{m.title}</h3>
                <p className="mvv-text">{m.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPANY DETAILS */}
      <section className="company-details">
        <div className="container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="section-tag">Company Profile</div>
            <h2 className="section-title">Official Company Details</h2>
          </motion.div>
          <div className="details-grid">
            {[
              { label: 'Registered Name', value: 'Seth Mr. Sanwariya Agro Primes' },
              { label: 'Business Type', value: 'Merchant Exporter' },
              { label: 'Founded', value: '22 October 2024' },
              { label: 'IEC Granted', value: '11 May 2026' },
              { label: 'Certifications', value: 'FSSAI, APEDA, IEC' },
              { label: 'Registered Address', value: 'C-18, Naveen Galla Mandi Sthal, Amanpur Road, Kasganj-207123, Uttar Pradesh, India' },
            ].map((d, i) => (
              <motion.div
                key={d.label}
                className="detail-card"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i * 0.08}
                variants={fadeUp}
              >
                <div className="detail-label">{d.label}</div>
                <div className="detail-value">{d.value}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta">
        <div className="container about-cta-inner">
          <h2>Ready to Partner with Us?</h2>
          <p>We welcome importers, wholesalers, and distributors from around the world.</p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '24px' }}>
            <Link to="/contact" className="btn-primary">Send an Inquiry</Link>
            <Link to="/products" className="btn-green-outline">View Our Products</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
